export interface Configuration {
    /**
     * The base URL for the onePay Hosted Fields. The payment
     * service will add the appropriate URL strings for the
     * card number and CVV fields.
     * 
     * Each client must provide this url
     */
    url?: string;

    /**
     * The host URL for the onePay Hosted Field. This used for postMessage.
     *
     * Optional. Will be extract host from url
     */
    host?: string;

    /**
     * The payment service client js will wait this long for the onePay 
     * Hosted Fields to respond after setting up the iframes
     */
    configTimeout?: number;

    /**
     * The payment service client js will wait this long for the onePay
     * Hosted Fields to respond after the submit message has been sent
     */
    submitTimeout?: number;

    /**
     * Configuration specific to the cardnumber field
     */
    cardNumber?: FieldInfo;

    /**
     * Configuration specific to the cvv field
     */
    securityCode?: FieldInfo;

    /**
     * The internal style of the onePay Hosted Fields will be updated to
     * one of these styles based on the calculated state of the field. 
     * New styles can be added to this list if a custom event handler 
     * requires them.
     * 
     * The default styles are:
     * default - The starting style prior to the field being validated
     * success - Set if the field passes validation
     * error   - Set if the field fails validation
     */
    styles: { [index: string]: Style };

    /**
     * These classes will be appended to the host DOM fields based on 
     * each user event
     */
    classNames: StyleClassNames;

    /**
     * An event handler provided for clients, called after each field event occurs
     */
    onFieldEvent?: (event: FieldEventArgs) => void;

    /**
     * An event handler provided for clients, called if a style change occurs
     */
    onStyleChangeEvent?: (event: StyleChangeEventArgs) => void;

    /**
     * An event handler provided for client, called if the card type change occurs
     */
    onCardTypeChangeEvent?: (args: CardTypeChangeEventArgs) => void;
}

/**
 * Configuration that will apply to a specific field
 */
export interface FieldInfo {
    /**
     * DOM element id
     */
    id: string; 

    /**
     * Placeholder text to be displayed by the hosted field
     */
    placeholder: string;

    /**
     * Character limit on the field
     */
    maxLength: number;

    /**
     * Default style to be used when initialising the field
     */
    defaultStyle: string;
}

/**
 * Settings to provide the onePay hosted field to set the 
 * internal style
 */
export interface Style {
    fontFamily: string;
    fontSize: string;
    fontWeight: string;
    color: string;
    padding: string;
    tooltipCC: string;
}

/**
 * Class names that will be applied on each style change
 */
export interface StyleClassNames {
    empty: string;
    invalid: string;
    valid: string;
    graceEnded: string;
    focus: string;
    touched: string;
}

export interface InputField {
    frame: HTMLIFrameElement;
    parent: HTMLElement;
}

export interface FormField {
    fieldName: string;
    fieldInfo: FieldInfo;
    isInitialized: boolean;
    style: string;
    hasFocus: boolean;
    state: FieldState;
    hasValidated: boolean;
    input: InputField
}

export interface FieldEventInfo {
    id: string;
    state: FieldState;
    hasFocus: boolean;
    hasValidated: boolean;
}

/**
 * Event parameter provided to onFieldEvent
 */
export interface FieldEventArgs {
    eventType: FieldEventType;
    field?: FieldEventInfo;
}

export interface CardTypeChangeEventArgs {
    cardType: string;
    panMask: string;
}

export interface StyleChangeEventArgs {
    field: FieldEventInfo;
    currentStyle: string;
    newStyle: string;
    styleCallback: (id: string, styleName: string) => void;
}

export interface CardInformation {
    cardHolderName: string;
    expiryMonth: number;
    expiryYear: number;
}

/**
 * The type of UI event
 */
export enum FieldEventType {
    blur = 'blur',
    focus = 'focus',
    stateChanged = 'stateChanged'
}

/**
 * The state of the field that fired the field event
 */
export enum FieldState {
    empty = 'empty',
    valid = 'valid',
    invalid = 'invalid'
}



// WEBPACK FOOTER //
// ./src/types.ts